/* eslint-disable @typescript-eslint/no-explicit-any */
import * as FileSaver from 'file-saver';
import { AxiosRequestConfig } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from '../shared/misc/axios';
import { fileReader } from '../shared/misc/utils';
import { msalInstance, loginRequest } from '../msalConfig';

const asSuperUserModeKey = 'esg_as_super_user_mode';
const asGuidanceServicerModeKey = 'esg_as_guidance_service_mode';

interface GlobalServiceScope {
  operationNumber?: string;
}
// es exporta esta variable por la imposibilidad de usar el contexto
// con use context en el lugar necesario
export const globalServiceScope: GlobalServiceScope = {};

export default class ServiceBase {
  expiresIn: number | null;

  static loginInProgress: boolean = false;

  constructor() {
    this.expiresIn = null;
    this.config = { headers: { Authorization: '' } };

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          sessionStorage.clear();
          localStorage.clear();
          msalInstance.logoutRedirect();
        }
        return Promise.reject(error);
      }
    );
    if (process.env.REACT_APP_SUBSCRIPTION_KEY) {
      this.config.headers['Ocp-Apim-Subscription-Key'] = process.env.REACT_APP_SUBSCRIPTION_KEY;
    }
  }

  private login = async () => {
    if (!ServiceBase.loginInProgress) {
      ServiceBase.loginInProgress = true;
      await msalInstance.loginRedirect(loginRequest);
    }
  };

  private getAccessToken = async (): Promise<string> => {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (!account) {
      // Si no hay cuenta, redirigimos al login
      await this.login();
      return '';
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: [process.env.REACT_APP_SCOPE || ''],
      });

      return response.accessToken;
    } catch (error: any) {
      const isInteractionRequired =
        error instanceof InteractionRequiredAuthError ||
        (error != null &&
          (error.errorCode === 'interaction_required' ||
            error.errorCode === 'login_required' ||
            error.errorCode === 'consent_required' ||
            error.errorCode === 'monitor_window_timeout'));

      if (isInteractionRequired) {
        // eslint-disable-next-line no-console
        console.warn('Token inválido o expirado, redirigiendo al login...');
        await this.login();
        return ''; // importante para cortar flujo
      }

      throw error;
    }
  };

  config: AxiosRequestConfig;

  setDefaultHeaders = async () => {
    this.config.headers['X-ESG-COMPASS-OPERATION-NUMBER'] = globalServiceScope.operationNumber
      ? globalServiceScope.operationNumber
      : '';

    const asSuperUserMode = sessionStorage.getItem(asSuperUserModeKey) || 'false';
    if (JSON.parse(asSuperUserMode) as boolean) {
      this.config.headers['X-ESG-COMPASS-AS-SUPERUSER'] = true;
    } else {
      this.config.headers['X-ESG-COMPASS-AS-SUPERUSER'] = false;
    }

    const asGuidanceServiceMode = sessionStorage.getItem(asGuidanceServicerModeKey) || 'false';
    if (JSON.parse(asGuidanceServiceMode) as boolean) {
      this.config.headers['X-ESG-COMPASS-AS-GUIDANCESERVICE'] = true;
    } else {
      this.config.headers['X-ESG-COMPASS-AS-GUIDANCESERVICE'] = false;
    }

    if (!process.env.REACT_APP_CLIENT_ID) {
      return;
    }

    const accessToken = await this.getAccessToken();

    if (accessToken) {
      this.config.headers.Authorization = `Bearer ${accessToken}`;
    }
  };

  delete = async <P>(url: string) => {
    await this.setDefaultHeaders();
    const result = await axios.delete<P>(url, this.config);
    return result.data;
  };

  get = async <P>(url: string) => {
    await this.setDefaultHeaders();
    const result = await axios.get<P>(url, this.config);
    return result.data;
  };

  post = async <P>(url: string, data?: any) => {
    await this.setDefaultHeaders();
    const result = await axios.post<P>(url, data, this.config);
    return result.data;
  };

  put = async <P>(url: string, data?: any) => {
    await this.setDefaultHeaders();
    const result = await axios.put<P>(url, data, this.config);
    return result.data;
  };

  download = async (url: string, data?: any, getType = false) => {
    await this.setDefaultHeaders();
    const config: AxiosRequestConfig = { ...this.config, responseType: 'blob', timeout: 300000 };

    try {
      const response = !getType
        ? await axios.post<Blob>(url, data, config)
        : await axios.get(url, config);
      const contentDisposition = response.headers['content-disposition'];
      const filename = (contentDisposition.match('filename=(.+);')[1] as string).replace(/"/g, '');
      FileSaver.saveAs(response.data, filename);
    } catch (error) {
      if (!error.response.data) {
        return;
      }
      const msg = (await fileReader(error.response.data)) as string;
      throw Error(msg);
    }
  };

  getBinary: (url: string) => Promise<string | null> = async (url: string) => {
    await this.setDefaultHeaders();
    const response = await axios.get<ArrayBuffer>(url, {
      ...this.config,
      responseType: 'arraybuffer',
    });
    if (response.data.byteLength === 0) {
      return null;
    }

    const result = `data:${response.headers['content-type']};base64,${Buffer.from(
      response.data
    ).toString('base64')}`;
    return result;
  };
}
